import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from "vue";

export const usePanelGridAutoHeight = ({
  desktopBreakpoint = 1024,
  panelBottomGap = 16,
  stickySelector = ".sticky.top-0",
} = {}) => {
  const panelGridRef = ref(null);
  const panelHeight = ref(null);

  const updatePanelHeight = () => {
    if (typeof window === "undefined") {
      return;
    }
    if (!panelGridRef.value) {
      panelHeight.value = null;
      return;
    }

    if (window.innerWidth < desktopBreakpoint) {
      panelHeight.value = null;
      return;
    }
    if (window.innerWidth >= desktopBreakpoint) {
      window.scrollTo({ top: 0, behavior: "auto" });
    }
    const { top } = panelGridRef.value.getBoundingClientRect();
    const availableHeight = window.innerHeight - top - panelBottomGap;
    panelHeight.value = availableHeight > 0 ? availableHeight : null;
  };

  const handleWindowResize = () => {
    updatePanelHeight();
  };

  let resizeListenerBound = false;
  const bindResizeListener = () => {
    if (typeof window === "undefined" || resizeListenerBound) {
      return;
    }
    window.addEventListener("resize", handleWindowResize);
    resizeListenerBound = true;
  };
  const unbindResizeListener = () => {
    if (typeof window === "undefined" || !resizeListenerBound) {
      return;
    }
    window.removeEventListener("resize", handleWindowResize);
    resizeListenerBound = false;
  };

  let stickyResizeObserver = null;
  let stickyObserverRetryTimer = null;
  const bindStickyObserver = () => {
    if (
      typeof window === "undefined" ||
      typeof ResizeObserver === "undefined" ||
      stickyResizeObserver
    ) {
      return;
    }
    const stickyTarget = document.querySelector(stickySelector);
    if (!stickyTarget) {
      if (!stickyObserverRetryTimer) {
        stickyObserverRetryTimer = window.setTimeout(() => {
          stickyObserverRetryTimer = null;
          bindStickyObserver();
        }, 300);
      }
      return;
    }

    stickyResizeObserver = new ResizeObserver(() => {
      updatePanelHeight();
    });
    stickyResizeObserver.observe(stickyTarget);
  };

  const unbindStickyObserver = () => {
    if (stickyObserverRetryTimer) {
      clearTimeout(stickyObserverRetryTimer);
      stickyObserverRetryTimer = null;
    }
    if (stickyResizeObserver) {
      stickyResizeObserver.disconnect();
      stickyResizeObserver = null;
    }
  };

  onMounted(() => {
    nextTick(() => {
      updatePanelHeight();
    });
    bindResizeListener();
    bindStickyObserver();
  });

  watch(
    () => panelGridRef.value,
    () => {
      nextTick(() => {
        updatePanelHeight();
      });
    }
  );

  onBeforeUnmount(() => {
    unbindResizeListener();
    unbindStickyObserver();
  });

  const panelCardStyle = computed(() => {
    if (!panelHeight.value) {
      return undefined;
    }
    const height = `${panelHeight.value}px`;
    return {
      height,
      maxHeight: height,
    };
  });

  return {
    panelGridRef,
    panelCardStyle,
    updatePanelHeight,
  };
};
